import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  fetchCart as fetchCartApi,
  replaceCart as replaceCartApi,
  addCartItems as addCartItemsApi,
  patchCartItem as patchCartItemApi,
  removeCartItem as removeCartItemApi,
  clearCart as clearCartApi,
} from '../api/checkout';
import useLatestRequest from '../hooks/useLatestRequest';
import { useUnifiedAuth } from './UnifiedAuthContext';
import {
  checkoutDebugLog,
  isCheckoutDebugEnabled,
  summarizeCartSnapshot,
} from '../utils/checkoutDebug';

const CheckoutCartContext = createContext(null);

const safeParse = (value) => {
  if (!value || typeof value !== 'object') {
    return null;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    console.warn('[CheckoutCartContext] Failed to clone metadata value', error);
    return null;
  }
};

export const CheckoutCartProvider = ({ children, autoLoad = true }) => {
  const { user, isAuthenticated, getApiToken } = useUnifiedAuth();
  const userId = user?.id;
  const { begin, cancel } = useLatestRequest();
  const session = useRef(0);
  const mutationQueue = useRef(Promise.resolve());
  const mutationCount = useRef(0);
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(autoLoad);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const debugSequenceRef = useRef(0);
  const cartSnapshotRef = useRef(null);
  const checkoutDebugEnabled = isCheckoutDebugEnabled;

  const getTokenIfAvailable = useCallback(async () => {
    if (!isAuthenticated) {
      return null;
    }
    const token = await getApiToken();
    if (!token) throw new Error('Authentication required to update checkout');
    return token;
  }, [getApiToken, isAuthenticated]);

  const refreshCart = useCallback(async () => {
    const signal = begin();
    const debugId = ++debugSequenceRef.current;
    const cartBefore = summarizeCartSnapshot(cartSnapshotRef.current);
    if (checkoutDebugEnabled) {
      checkoutDebugLog('info', `refreshCart:start #${debugId}`, {
        authenticated: isAuthenticated,
        cartBefore,
      });
      setDebugInfo({
        type: 'refreshCart',
        debugId,
        status: 'pending',
        timestamp: Date.now(),
        cartBefore,
        authenticated: isAuthenticated,
      });
    }
    setLoading(true);
    setError(null);
    try {
      // Reads must observe completed writes; an older read cannot undo a cart edit.
      await mutationQueue.current;
      if (signal.aborted) return;
      const token = await getTokenIfAvailable();
      if (signal.aborted) return;
      const data = await fetchCartApi(token, signal);
      if (signal.aborted) return;
      setCart(data);
      cartSnapshotRef.current = data;
      if (checkoutDebugEnabled) {
        checkoutDebugLog('info', `refreshCart:success #${debugId}`, {
          cartAfter: summarizeCartSnapshot(data),
        });
        setDebugInfo({
          type: 'refreshCart',
          debugId,
          status: 'success',
          timestamp: Date.now(),
          cartBefore,
          cartAfter: summarizeCartSnapshot(data),
        });
      }
    } catch (fetchError) {
      if (signal.aborted) return;
      console.error('[CheckoutCartContext] Failed to load cart', fetchError);
      setError(fetchError);
      setCart(null);
      cartSnapshotRef.current = null;
      if (checkoutDebugEnabled) {
        checkoutDebugLog('warn', `refreshCart:error #${debugId}`, {
          message: fetchError?.message || 'unknown error',
          status: fetchError?.status ?? null,
        });
        setDebugInfo({
          type: 'refreshCart',
          debugId,
          status: 'error',
          timestamp: Date.now(),
          cartBefore,
          message: fetchError?.message || 'unknown error',
          statusCode: fetchError?.status ?? null,
        });
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [checkoutDebugEnabled, getTokenIfAvailable, isAuthenticated, begin]);

  useEffect(() => {
    setCart(null);
    cartSnapshotRef.current = null;
    setError(null);
    setDebugInfo(null);
    // Child mount effects may already have queued a write for this session.
    setPending(mutationCount.current > 0);
    if (autoLoad) void refreshCart();
    else setLoading(false);
    return () => {
      session.current += 1;
      mutationCount.current = 0;
      cancel();
    };
  }, [autoLoad, refreshCart, cancel, userId]);

  const runMutation = useCallback(
    (action, mutationFn, debugContextBuilder) => {
      const owner = session.current;
      cancel();
      setLoading(false);
      mutationCount.current += 1;
      setPending(true);
      // Serialize writes, rather than merely ignoring out-of-order responses: the
      // server must receive quantity/plan edits in the order the customer made them.
      const operation = mutationQueue.current.then(async () => {
        if (owner !== session.current) throw new Error('Checkout session changed');
        const debugId = ++debugSequenceRef.current;
        const cartBefore = summarizeCartSnapshot(cartSnapshotRef.current);
        const builderOutput =
          typeof debugContextBuilder === 'function' ? debugContextBuilder(cartBefore) : {};
        if (checkoutDebugEnabled) {
          checkoutDebugLog('info', `${action}:start #${debugId}`, {
            cartBefore,
            ...builderOutput,
          });
          setDebugInfo({
            type: action,
            debugId,
            status: 'pending',
            timestamp: Date.now(),
            cartBefore,
            payloadSummary: builderOutput?.payloadSummary ?? null,
            productSlug: builderOutput?.productSlug ?? null,
            flowContext: builderOutput?.flowContext ?? null,
          });
        }
        setError(null);
        try {
          const token = await getTokenIfAvailable();
          if (owner !== session.current) throw new Error('Checkout session changed');
          const updated = await mutationFn(token);
          if (owner !== session.current) return updated;
          setCart(updated);
          cartSnapshotRef.current = updated;
          if (checkoutDebugEnabled) {
            checkoutDebugLog('info', `${action}:success #${debugId}`, {
              cartBefore,
              cartAfter: summarizeCartSnapshot(updated),
            });
            setDebugInfo({
              type: action,
              debugId,
              status: 'success',
              timestamp: Date.now(),
              cartBefore,
              cartAfter: summarizeCartSnapshot(updated),
              payloadSummary: builderOutput?.payloadSummary ?? null,
              productSlug: builderOutput?.productSlug ?? null,
              flowContext: builderOutput?.flowContext ?? null,
            });
          }
          return updated;
        } catch (mutationError) {
          if (owner !== session.current) throw mutationError;
          console.error('[CheckoutCartContext] Cart mutation failed', mutationError);
          setError(mutationError);
          if (checkoutDebugEnabled) {
            checkoutDebugLog('warn', `${action}:error #${debugId}`, {
              cartBefore,
              message: mutationError?.message || 'unknown error',
              status: mutationError?.status ?? null,
            });
            setDebugInfo({
              type: action,
              debugId,
              status: 'error',
              timestamp: Date.now(),
              cartBefore,
              message: mutationError?.message || 'unknown error',
              statusCode: mutationError?.status ?? null,
              payloadSummary: builderOutput?.payloadSummary ?? null,
              productSlug: builderOutput?.productSlug ?? null,
              flowContext: builderOutput?.flowContext ?? null,
            });
          }
          throw mutationError;
        } finally {
          if (owner === session.current) {
            mutationCount.current -= 1;
            setPending(mutationCount.current > 0);
          }
        }
      });
      // A rejected operation still releases the queue for the next customer edit.
      mutationQueue.current = operation.then(
        () => undefined,
        () => undefined,
      );
      return operation;
    },
    [checkoutDebugEnabled, getTokenIfAvailable, cancel],
  );

  const replaceCart = useCallback(
    (items, options = {}) => {
      const payload = {
        items,
        flowContext: options.flowContext ?? cartSnapshotRef.current?.flowContext ?? null,
        metadata: options.metadata
          ? safeParse(options.metadata)
          : (cartSnapshotRef.current?.metadata ?? null),
      };
      return runMutation(
        'replaceCart',
        (token) => replaceCartApi(payload, token),
        () => ({
          payloadSummary: {
            itemCount: Array.isArray(items) ? items.length : 0,
            flowContext: payload.flowContext,
            hasMetadata: Boolean(payload.metadata),
          },
        }),
      );
    },
    [runMutation],
  );

  const addItems = useCallback(
    (items, options = {}) => {
      const payload = {
        items,
        flowContext: options.flowContext ?? null,
        metadata: options.metadata ? safeParse(options.metadata) : undefined,
      };
      return runMutation(
        'addItems',
        (token) => addCartItemsApi(payload, token),
        () => ({
          payloadSummary: {
            itemCount: Array.isArray(items) ? items.length : 0,
            flowContext: payload.flowContext,
            hasMetadata: Boolean(payload.metadata),
          },
        }),
      );
    },
    [runMutation],
  );

  const updateItem = useCallback(
    (productSlug, update, options = {}) => {
      const payload = {
        ...update,
        flowContext: options.flowContext ?? null,
        cartMetadata: options.metadata ? safeParse(options.metadata) : undefined,
      };
      return runMutation(
        'updateItem',
        (token) => patchCartItemApi(productSlug, payload, token),
        () => ({
          productSlug,
          payloadSummary: {
            fields: Object.keys(update || {}),
            flowContext: payload.flowContext,
            hasMetadata: Boolean(payload.cartMetadata),
          },
        }),
      );
    },
    [runMutation],
  );

  const removeItem = useCallback(
    (productSlug) =>
      runMutation(
        'removeItem',
        (token) => removeCartItemApi(productSlug, token),
        () => ({
          productSlug,
        }),
      ),
    [runMutation],
  );

  const clearCart = useCallback(
    () =>
      runMutation(
        'clearCart',
        (token) => clearCartApi(token),
        () => ({}),
      ),
    [runMutation],
  );

  const setFlowContext = useCallback(
    (flowContext) =>
      runMutation(
        'setFlowContext',
        (token) => addCartItemsApi({ items: [], flowContext }, token),
        () => ({
          flowContext,
        }),
      ),
    [runMutation],
  );

  useEffect(() => {
    cartSnapshotRef.current = cart;
  }, [cart]);

  const value = useMemo(
    () => ({
      cart,
      loading,
      pending,
      error,
      refreshCart,
      replaceCart,
      addItems,
      updateItem,
      removeItem,
      clearCart,
      setFlowContext,
      debugInfo: checkoutDebugEnabled ? debugInfo : null,
      debugEnabled: checkoutDebugEnabled,
    }),
    [
      cart,
      loading,
      pending,
      error,
      refreshCart,
      replaceCart,
      addItems,
      updateItem,
      removeItem,
      clearCart,
      setFlowContext,
      checkoutDebugEnabled,
      debugInfo,
    ],
  );

  return <CheckoutCartContext.Provider value={value}>{children}</CheckoutCartContext.Provider>;
};

export const useCheckoutCart = () => {
  const context = useContext(CheckoutCartContext);
  if (!context) {
    throw new Error('useCheckoutCart must be used within a CheckoutCartProvider');
  }
  return context;
};
